import React, { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

// ——— Custom Styles ———————————————————
const CustomStyles = () => (
  <style>{`
    body { 
      margin: 0; 
      -webkit-font-smoothing: antialiased; 
      -moz-osx-font-smoothing: grayscale; 
      background-color: #0A0A0B;
    }
    .liquid-bg {
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: radial-gradient(circle at 20% 30%, rgba(249, 115, 22, 0.15) 0%, transparent 40%),
                  radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 40%);
      z-index: 0;
      pointer-events: none;
    }
  `}</style>
);

// ——— Helper Components ———————————————————————————————
const Card = ({ title, children, className = "" }) => (
  <div className={`bg-white/[0.02] backdrop-blur-3xl rounded-3xl p-6 md:p-8 border border-white/10 shadow-[0_8px_32px_0_rgba(0,0,0,0.3)] transition-transform duration-500 hover:bg-white/[0.04] ${className}`}>
    {title && <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 md:mb-6 tracking-tight">{title}</h3>}
    {children}
  </div>
);

// Count-up for hero KPIs
const NumberCounter = ({ value, suffix = "", label }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const end = typeof value === "number" ? value : 0;
    let raf; const start = performance.now(); const dur = 1200;
    const loop = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const easeOutQuart = 1 - Math.pow(1 - p, 4);
      setCount(Math.floor(end * easeOutQuart));
      if (p < 1) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <div>
      <p className="text-4xl md:text-5xl font-black bg-gradient-to-r from-orange-400 to-orange-200 bg-clip-text text-transparent tracking-tight leading-tight">{count.toLocaleString()}{suffix}</p>
      <p className="text-white/50 mt-2 text-sm uppercase tracking-widest font-semibold">{label}</p>
    </div>
  );
};

// ——— Data ————————————————
const filmData = [
  { url: "https://www.instagram.com/p/DWTvzd4jGTk/", views: 10335, likes: 124, comments: 0, owner: "bollywoodzoo" },
  { url: "https://www.instagram.com/p/DWjcYn1iGF3/", views: 15554, likes: 368, comments: 0, owner: "filmymeme" },
  { url: "https://www.instagram.com/p/DWUT1rsgT5N/", views: 10366, likes: 153, comments: 0, owner: "filmi_fiesta" },
  { url: "https://www.instagram.com/p/DWgJCY2oL0Y/", views: 67789, likes: 126, comments: 0, owner: "filmytrollz" },
  { url: "https://www.instagram.com/p/DWT1_DqiF2-/", views: 40312, likes: 234, comments: 0, owner: "filmyzoo" },
  { url: "https://www.instagram.com/p/DWgLrWQDVdG/", views: 11156, likes: 1, comments: 0, owner: "the.bollywood.dose" },
  { url: "https://www.instagram.com/p/DWjpblQE5-h/", views: 3408, likes: 3, comments: 0, owner: "bollywoodrecommendation" },
  { url: "https://www.instagram.com/p/DWgK1KxjXJP/", views: 15214, likes: 3, comments: 0, owner: "the.bollywood.insider" },
  { url: "https://www.instagram.com/p/DWjQD-rE3cl/", views: 4684, likes: 128, comments: 0, owner: "filmygoalz" },
  { url: "https://www.instagram.com/p/DWjcEZnjCK_/", views: 2456, likes: 136, comments: 0, owner: "filmyzig" },
  { url: "https://www.instagram.com/p/DWT1VA4gjBa/", views: 20372, likes: 122, comments: 0, owner: "bollywoodstarstruck" },
  { url: "https://www.instagram.com/p/DWjt5hfk5_e/", views: 31369, likes: 244, comments: 0, owner: "90s.muzic" },
  { url: "https://www.instagram.com/p/DWjQVc1kU_E/", views: 3917, likes: 163, comments: 0, owner: "bollywoodmagiz" },
  { url: "https://www.instagram.com/p/DWTwHIMCG2I/", views: 508185, likes: 0, comments: 30, owner: "filmyactive" },
  { url: "https://www.instagram.com/p/DWT2By-D9AB/", views: 10294, likes: 113, comments: 0, owner: "btown_talkz" },
  { url: "https://www.instagram.com/p/DWjcWilDNFn/", views: 3104, likes: 117, comments: 0, owner: "thebollywoodgags" },
  { url: "https://www.instagram.com/p/DWT2uUpCQUg/", views: 10163, likes: 115, comments: 0, owner: "daily.filmy.dose" },
  { url: "https://www.instagram.com/p/DWgJ43GjdHb/", views: 25988, likes: 2, comments: 0, owner: "thefilmyvilla" },
  { url: "https://www.instagram.com/p/DWjcSAekz7f/", views: 15371, likes: 0, comments: 2, owner: "filmydodo" },
  { url: "https://www.instagram.com/p/DWT7oIuCOvO/", views: 10253, likes: 126, comments: 0, owner: "bollywoodcastic" },
  { url: "https://www.instagram.com/p/DWjp40ijNN1/", views: 4612, likes: 130, comments: 0, owner: "thebollywoodcouples" },
  { url: "https://www.instagram.com/p/DWjS1lkkVFC/", views: 6877, likes: 236, comments: 0, owner: "bollywoodcasm" },
  { url: "https://www.instagram.com/p/DWjPjVnyZQf/", views: 5483, likes: 260, comments: 0, owner: "filmytown.in" },
  { url: "https://www.instagram.com/p/DWjpedNgGPX/", views: 1793, likes: 128, comments: 0, owner: "ibollywooddaily" },
  { url: "https://www.instagram.com/p/DWjpKEoiRBh/", views: 17883, likes: 2, comments: 0, owner: "bollywoodglimpse" },
  { url: "https://www.instagram.com/p/DWjQMVnkmqM/", views: 5141, likes: 147, comments: 0, owner: "filmifanatic" },
  { url: "https://www.instagram.com/p/DWjQkTSkmHW/", views: 5939, likes: 205, comments: 0, owner: "thefilmyaccess" },
  { url: "https://www.instagram.com/p/DWT8LYYDKk3/", views: 10102, likes: 109, comments: 0, owner: "thefilmydays" },
  { url: "https://www.instagram.com/p/DWjcQwMkhm2/", views: 2379, likes: 135, comments: 0, owner: "thefilmyfactory" },
  { url: "https://www.instagram.com/p/DWjpmnEkWds/", views: 6474, likes: 156, comments: 0, owner: "strictlyforbollywood" },
  { url: "https://www.instagram.com/p/DWgJFE6EUoA/", views: 68574, likes: 8, comments: 0, owner: "filmyglams" },
  { url: "https://www.instagram.com/p/DWjaaOrgtmX/", views: 8112, likes: 0, comments: 0, owner: "indiamoviepage" },
  { url: "https://www.instagram.com/p/DWjb6uzEufQ/", views: 5229, likes: 148, comments: 0, owner: "ottwhoopy" },
  { url: "https://www.instagram.com/p/DWTvpxOEcnR/", views: 506095, likes: 3375, comments: 24, owner: "filmydhun" }
];

const marketingData = [
  { url: "https://www.instagram.com/p/DWgxfa_CNNs/", views: 18453, likes: 200, comments: 0, owner: "indiasstartup" },
  { url: "https://www.instagram.com/p/DWgxZ8XiCVl/", views: 49261, likes: 300, comments: 0, owner: "startup_universe" },
  { url: "https://www.instagram.com/p/DWgyEKCCODe/", views: 25636, likes: 150, comments: 0, owner: "startup.hack" },
  { url: "https://www.instagram.com/p/DWgx5q0k2oT/", views: 22390, likes: 150, comments: 0, owner: "marketingbuzz.india" },
  { url: "https://www.instagram.com/p/DWgyQeAjG4J/", views: 28949, likes: 10, comments: 0, owner: "startupshivay" },
  { url: "https://www.instagram.com/p/DWg_rapAkOn/", views: 25058, likes: 200, comments: 0, owner: "startuphours" },
  { url: "https://www.instagram.com/p/DWgyuw6BACM/", views: 21018, likes: 250, comments: 0, owner: "financeupmedia" },
  { url: "https://www.instagram.com/p/DWgxatvCVK5/", views: 91520, likes: 500, comments: 0, owner: "startups_media" },
  { url: "https://www.instagram.com/p/DWg2y6ujK3V/", views: 45421, likes: 83, comments: 0, owner: "startupindiastories" },
  { url: "https://www.instagram.com/p/DWg9k1xjiyM/", views: 13226, likes: 22, comments: 0, owner: "businessbyceo" },
  { url: "https://www.instagram.com/p/DWgxgU4Ed4u/", views: 100797, likes: 800, comments: 0, owner: "investupmedia" },
  { url: "https://www.instagram.com/p/DWbwv7clD7C/", views: 25000, likes: 100, comments: 3, owner: "startupbydoc" } 
];

const totalFilmyViews = filmData.reduce((acc, curr) => acc + curr.views, 0);
const totalMarketingViews = marketingData.reduce((acc, curr) => acc + curr.views, 0);
const allViews = totalFilmyViews + totalMarketingViews;

const totalLinks = filmData.length + marketingData.length;

// Data for charts
const pieData = [
  { name: 'Filmy Network', value: totalFilmyViews },
  { name: 'Marketing Network', value: totalMarketingViews },
];
const COLORS = ['#f97316', '#ffffff'];

const combinedChartData = [
  ...filmData.map(d => ({ name: d.owner, views: d.views, category: 'Filmy' })),
  ...marketingData.map(d => ({ name: d.owner, views: d.views, category: 'Marketing' }))
].sort((a,b) => b.views - a.views).slice(0, 10); 

// ——— Table Component ————————————————————————
const DatasetTable = ({ title, data, tagColor }) => (
  <Card className="p-0 md:p-0 overflow-hidden mb-8">
    <div className="p-6 border-b border-white/5 flex items-center gap-3">
      <div className={`w-3 h-3 rounded-full ${tagColor}`} />
      <h3 className="text-xl font-bold text-white tracking-tight">{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-[760px] w-full text-left text-sm">
        <thead className="bg-white/5">
          <tr className="border-b border-white/10">
            <th className="px-6 py-4 uppercase tracking-wider text-white/40 text-xs font-semibold">Page / Handle</th>
            <th className="px-6 py-4 uppercase tracking-wider text-white/40 text-xs font-semibold text-right">Total Views</th>
            <th className="px-6 py-4 uppercase tracking-wider text-white/40 text-xs font-semibold text-right">Estimated Likes</th>
            <th className="px-6 py-4 uppercase tracking-wider text-white/40 text-xs font-semibold">Link</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
              <td className="px-6 py-4 font-medium text-white/90">@{r.owner}</td>
              <td className="px-6 py-4 text-right font-mono text-orange-200 font-semibold">{r.views.toLocaleString()}</td>
              <td className="px-6 py-4 text-right font-mono text-white/50">{r.likes > 0 ? r.likes.toLocaleString() : "Hidden"}</td>
              <td className="px-6 py-4">
                <a href={r.url} target="_blank" rel="noreferrer" className="text-xs px-4 py-2 bg-orange-500/10 hover:bg-orange-500/20 text-orange-300 font-medium rounded-full transition-colors whitespace-nowrap inline-flex items-center gap-1">
                  View Post ↗
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

// ——— Page Shell ——————————————
export default function TVSReport() {
  useEffect(() => {
    document.title = "TVS Launch – Campaign Report";
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white selection:bg-orange-500/30 font-sans relative overflow-x-hidden">
      <CustomStyles />
      <div className="liquid-bg" />
      
      <div className="relative z-10">
        <header className="px-6 py-6 border-b border-white/5">
          <div className="container mx-auto flex justify-between items-center">
             <span className="font-semibold tracking-[0.2em] text-white/30 text-xs uppercase">Social SEO Inc.</span>
             <span className="font-semibold tracking-[0.2em] text-orange-400/50 text-xs uppercase">Client Portal</span>
          </div>
        </header>

        <main className="container mx-auto px-4 py-12 md:py-24 max-w-6xl">
          
          {/* Header Section */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full text-xs font-bold text-orange-300 tracking-widest uppercase mb-6 shadow-[0_0_20px_rgba(99,102,241,0.2)]">
               <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
               Live Campaign Report
            </div>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[1.1] mb-6">
              TVS Scooter <br/>
              <span className="bg-gradient-to-r from-orange-300 via-white to-white bg-clip-text text-transparent">Launch Network</span>
            </h1>
            <p className="text-lg md:text-xl text-white/50 max-w-2xl mx-auto font-medium">
              Unified performance analytics across Filmy and Marketing portfolios. Multi-channel distribution for #TVSX.
            </p>
          </div>

          {/* Core KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
            <Card>
              <NumberCounter value={allViews} label="Total Views Generated" />
            </Card>
            <Card>
              <NumberCounter value={totalFilmyViews} label="Filmy Network Views" />
            </Card>
            <Card>
              <NumberCounter value={totalMarketingViews} label="Marketing Network Views" />
            </Card>
            <Card>
              <NumberCounter value={totalLinks} label="Active Promotions" />
            </Card>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-20">
            <Card title="Traffic by Network" className="lg:col-span-1 flex flex-col items-center justify-center">
              <div className="h-[250px] w-full flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value" stroke="none">
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                       contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                       itemStyle={{ color: '#fff' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-4 mt-2">
                 <div className="flex items-center gap-2 text-xs text-white/60"><div className="w-3 h-3 rounded-full bg-[#f97316]" /> Filmy ({Math.round(totalFilmyViews/allViews*100)}%)</div>
                 <div className="flex items-center gap-2 text-xs text-white/60"><div className="w-3 h-3 rounded-full bg-[#ffffff]" /> Marketing ({Math.round(totalMarketingViews/allViews*100)}%)</div>
              </div>
            </Card>
            
            <Card title="Top 10 Performing Pages" className="lg:col-span-2">
              <div className="h-[250px] w-full mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={combinedChartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="name" stroke="rgba(255,255,255,0.3)" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="rgba(255,255,255,0.3)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip 
                      cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                      contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }}
                    />
                    <Bar dataKey="views" radius={[4, 4, 0, 0]}>
                      {combinedChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.category === 'Filmy' ? '#f97316' : '#ffffff'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Tables Section */}
          <div className="space-y-8">
            <DatasetTable title="Filmy Pages Breakdown" data={filmData} tagColor="bg-[#f97316]" />
            <DatasetTable title="Marketing Pages Breakdown" data={marketingData} tagColor="bg-[#ffffff]" />
          </div>

        </main>

        <footer className="text-center py-16 border-t border-white/5 text-white/30 text-xs mt-12 bg-black/20">
            <p className="tracking-widest uppercase">Social SEO Inc. • Campaign Operations</p>
        </footer>
      </div>
    </div>
  );
}
